import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, CheckCircle2, XCircle, Clock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';

interface RecentEvaluation {
  id: string;
  source_id: string;
  status: string;
  score: number | null;
  ai_model: string;
  created_at: string;
  source_name: string;
}

export function RecentEvals() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const { data: recentEvals, isLoading } = useQuery({
    queryKey: ['recent-evals', user?.id],
    queryFn: async (): Promise<RecentEvaluation[]> => {
      if (!user) return [];

      const { data: evaluations, error } = await supabase
        .from('evaluations')
        .select('id, source_id, status, score, ai_model, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      if (!evaluations || evaluations.length === 0) return [];

      const sourceIds = evaluations.map((e) => e.source_id);
      const { data: sources } = await supabase
        .from('sources')
        .select('id, file_name, title')
        .in('id', sourceIds);

      const sourceMap = new Map(
        (sources ?? []).map((s) => [s.id, s.title || s.file_name])
      );

      return evaluations.map((e) => ({
        ...e,
        source_name: sourceMap.get(e.source_id) ?? 'Unknown',
      }));
    },
    enabled: !!user,
  });

  const statusConfig = {
    completed: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    failed: { icon: XCircle, color: 'text-rose-600', bg: 'bg-rose-50' },
    processing: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    pending: { icon: Clock, color: 'text-slate-400', bg: 'bg-slate-50' },
  };

  function getScoreColor(score: number): string {
    if (score >= 70) return 'text-emerald-600 bg-emerald-50';
    if (score >= 40) return 'text-amber-600 bg-amber-50';
    return 'text-rose-600 bg-rose-50';
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Evaluations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-8 w-8 animate-pulse rounded bg-slate-200" />
                <div className="flex-1 space-y-1">
                  <div className="h-4 w-40 animate-pulse rounded bg-slate-200" />
                  <div className="h-3 w-24 animate-pulse rounded bg-slate-200" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Recent Evaluations</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/evaluate')}
          className="text-indigo-600"
        >
          View All
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {!recentEvals || recentEvals.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-500">
            <FileText className="mx-auto mb-2 h-8 w-8 text-slate-300" />
            No evaluations yet. Start by evaluating your sources.
          </div>
        ) : (
          <div className="space-y-3">
            {recentEvals.map((evaluation) => {
              const config =
                statusConfig[evaluation.status as keyof typeof statusConfig] ??
                statusConfig.pending;
              const StatusIcon = config.icon;

              return (
                <button
                  key={evaluation.id}
                  onClick={() => navigate(`/results/${evaluation.id}`)}
                  className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-slate-50"
                >
                  <div className={cn('rounded-lg p-2', config.bg)}>
                    <StatusIcon className={cn('h-4 w-4', config.color)} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {evaluation.source_name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {format(new Date(evaluation.created_at), 'MMM d, HH:mm')}
                      {' · '}
                      {evaluation.ai_model === 'gpt-4-turbo' ? 'GPT-4' : 'Claude'}
                    </p>
                  </div>

                  {evaluation.score !== null && (
                    <Badge
                      variant="secondary"
                      className={cn('font-mono', getScoreColor(evaluation.score))}
                    >
                      {evaluation.score.toFixed(0)}%
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
